import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Table,
    Sheet,
    Chip
} from '@mui/joy';
import AssessmentIcon from '@mui/icons-material/Assessment';
import axiosConfig from '../util/axiosConfig';

const tableSheetSx = {
    width: '100%',
    maxWidth: '100%',
    borderRadius: 'sm',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '600px',
    WebkitOverflowScrolling: 'touch',
    border: '1px solid',
    borderColor: 'divider'
};

const assessmentTableSx = {
    width: 'max-content',
    minWidth: '100%',
    tableLayout: 'fixed',
    '& tbody tr:nth-of-type(even)': {
        backgroundColor: '#f9f9f9'
    },
    '& tbody tr:hover': {
        backgroundColor: '#fff5ed'
    },
    '& thead': {
        position: 'sticky',
        top: 0,
        zIndex: 10
    },
    '& thead th': {
        backgroundColor: '#eb7a24',
        color: 'white',
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderBottom: '2px solid #e55125',
        whiteSpace: 'nowrap'
    },
    '& td, & th': {
        padding: '12px',
        border: '1px solid',
        borderColor: 'divider',
        verticalAlign: 'top',
        overflow: 'hidden'
    },
    '& th:nth-of-type(1), & td:nth-of-type(1)': {
        width: 120,
        minWidth: 120,
        whiteSpace: 'nowrap'
    },
    '& th:nth-of-type(2), & td:nth-of-type(2)': {
        width: 200,
        minWidth: 200,
        whiteSpace: 'normal',
        wordBreak: 'break-word'
    },
    '& th:nth-of-type(3), & td:nth-of-type(3)': {
        width: 140,
        minWidth: 140,
        whiteSpace: 'nowrap'
    },
    '& th:nth-of-type(4), & td:nth-of-type(4)': {
        width: 160,
        minWidth: 160,
        whiteSpace: 'normal',
        wordBreak: 'break-word'
    }
};

const cellTextSx = {
    whiteSpace: 'inherit',
    wordBreak: 'inherit'
};

const AssessmentStatus = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [assessments, setAssessments] = useState([]);

    useEffect(() => {
        fetchAssessmentStatus();
    }, []);

    const fetchAssessmentStatus = async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/assessment-status');
            setAssessments(response.data.assessments || []);
        } catch (err) {
            console.error('Error fetching assessment status:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load assessment status data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading assessment status...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            </Box>
        );
    }

    if (assessments.length === 0) {
        return (
            <Box sx={{ p: 2, maxWidth: '1400px', mx: 'auto', width: '100%', minWidth: 0 }}>
                <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                    <Typography level="h4" sx={{ fontWeight: 'bold', color: '#6e7072' }}>
                        Assessment not yet scheduled
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: '1400px', mx: 'auto', width: '100%', minWidth: 0 }}>
            <Card variant="outlined">
                <CardContent sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <AssessmentIcon sx={{ color: '#eb7a24', fontSize: '1.5rem' }} />
                        <Typography level="h3" sx={{ color: '#eb7a24', fontWeight: 'bold' }}>
                            Assessment Status
                        </Typography>
                    </Box>
                    <Sheet variant="outlined" sx={tableSheetSx}>
                        <Table
                            aria-label="assessment status table"
                            sx={assessmentTableSx}
                        >
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Module</th>
                                    <th>Status</th>
                                    <th>Faculty</th>
                                </tr>
                            </thead>
                            <tbody>
                                {assessments.map((assessment, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Typography level="body-md" sx={cellTextSx}>
                                                {assessment.date || '-'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-md" sx={cellTextSx}>
                                                {assessment.module || '-'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Chip
                                                color={assessment.statusColor}
                                                size="sm"
                                                variant="soft"
                                            >
                                                {assessment.status || '-'}
                                            </Chip>
                                        </td>
                                        <td>
                                            <Typography level="body-md" sx={cellTextSx}>
                                                {assessment.faculty || '-'}
                                            </Typography>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                </CardContent>
            </Card>
        </Box>
    );
};

export default AssessmentStatus;

